import { Col, DatePicker, Layout, Row, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumbers,
  GetAllAccomodations,
  GetAllShips,
  getDateRange,
  handleChangePage,
  handleExportToExcel,
  handleExportToPDF,
  handlePageSizeChange,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { useEffect, useState } from "react";
import { getRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ExpenseReport = () => {
  const [loading, setLoading] = useState("");
  const dateRange = getDateRange("month");
  const [filterConfig, setFilterConfig]: any = useState({
    page: 1,
    date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
    date_to: formatDate(dateRange[1], "YYYY-MM-DD"),
  });
  const [dataSize, setDataSize] = useState(0);
  const [dataSource, setDataSource]: any = useState([]);
  const [branches, setBranches]: any = useState([]);

  useEffect(() => {
    fetchTableData();
  }, [filterConfig]);

  const fetchTableData = async () => {
    setLoading("fetching");
    try {
      const response = await getRequest("reports/get_expenses", filterConfig);
      if (response?.success) {
      
        const responseData = response.data?.data || [];
        const totalCount = response.data?.total || response.data?.data?.length || 0;
        
        setDataSource(Array.isArray(responseData) ? responseData : []);
        setDataSize(totalCount);
      } else {
        setDataSource([]);
        setDataSize(0);
      }
    // const sampleData = {
    //   "size": 1,
    //   "data": [
    //       {
    //           id: "7",
    //           purchase_request_id: "2",
    //           supplier_id: "1",
    //           branch_id: "1",
    //           request_date: "2026-01-09",
    //           grand_total: "9990.0000",
    //           paid_amount: "0.0000",
    //           remarks: null,
    //           status: "pending",
    //           disapproved_reason: null,
    //           reviewed_by: null,
    //           reviewed_on: null,
    //           added_by: "10",
    //           added_on: "2026-01-09 13:54:42",
    //           updated_by: null,
    //           updated_on: null,
    //           is_deleted: "0",
    //           branch_name: "Emborg Whole Milk",
    //           supplier_name: "test supplier",
    //           added_by_name: "MYT Accounting",
    //           reviewer_name: null
    //       }
    //   ]
    // };

    // const responseData = sampleData;
    // const totalCount = sampleData.size;

    // setDataSource([responseData]);
    // setDataSize(totalCount);

    } catch (error) {
      console.error("Error fetching expense report:", error);
    } finally {
      setLoading("");
    }
  };

  type SaleRecord = {
    added_by_name: string;
    added_on: string;
    request_date: string;
    paid_amount: string;
  };

  const columns: ColumnsType<SaleRecord> = [
    {
      title: "Ref No.",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Transaction Date",
      dataIndex: "request_date",
      key: "request_date",
      render: (date: any) => formatDate(date, "MMM DD, YYYY h:mm A"),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
    },
    {
      title: "Amount",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right" as any,
      render: (value: any) => formatNumbers(value),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
    },
    {
      title: "Branch",
      dataIndex: "branch_name",
      key: "branch_name",
    },
    {
      title: "Added By",
      dataIndex: "added_by_name",
      key: "added_by_name"
    },
  ];

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: formatDate(value[0], "YYYY-MM-DD"),
        date_to: formatDate(value[1], "YYYY-MM-DD"),
      }));
    } else {
      setFilterConfig((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  }

  function exportToExcel() {
    handleExportToExcel(dataSource, columns, "ExpenseReport");
  }

  function exportToPDF() {
    handleExportToPDF(
      dataSource,
      columns,
      "ExpenseReport.pdf",
      {
        title: "Expense Report",
        content: `Generated on ${new Date().toLocaleString()}`,
      },
      `Total Records: ${dataSource.length}`
    );
  }

  async function fetchBranches() {
    setBranches([]);
    setLoading("fetch");
    const response = await getRequest(`branches/get`, true);
    if (response.success) {
      var data = response.data.data.map((info: any) => ({
        ...info,
        branch_id: info.id,
        branch_name: info.name,
      }));
      setBranches(data);
    }

    setLoading("");
  }


  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Expense Report"
              withDateRangeFilter={false}
              withAddButton={false}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={() => exportToExcel()}
              onExportToPDF={() => exportToPDF()}
              withSearchBtn={true}
              searchPlaceholder="Search supplier"
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value,
                }))
              }
            />
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col span={24} className="mb-3">
            <Row>
              <Col xs={24} md={12} lg={6} className="mb-2 me-2 mb-lg-0">
                <RangePicker
                  value={[
                    filterConfig.date_from
                      ? dayjs(filterConfig.date_from)
                      : null,
                    filterConfig.date_to ? dayjs(filterConfig.date_to) : null,
                  ]}
                  format="MMMM DD, YYYY"
                  onChange={(e) => handleFilterChange(e, "dates")}
                  allowClear
                  className="w-full"
                />
              </Col>
              <Col xs={24} md={12} lg={5} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select branch"
                  value={filterConfig.branch_id || ""}
                  onChange={(value) => handleFilterChange(value, "branch_id")}
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Branches</Option>
                  {branches.map((branch: any) => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <div className="table-container">
              <Row className="mb-3 mx-2">
                <Col xs={24} lg={8} className="font-bold">
                  Total Expenses:
                </Col>
              </Row>
              <CustomTable
                data={dataSource}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{ x: "1000px" }}
                pagination={{
                  current: filterConfig.page,
                  total: dataSize,
                  showSizeChanger: true,
                  onChange: (page: number) =>
                    handleChangePage(page, setFilterConfig),
                  onShowSizeChange: (_: number, size: number) =>
                    handlePageSizeChange(size, setFilterConfig),
                }}
                handleRowClick={(record: any) => {}}
              />
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default ExpenseReport;
